/**
 * Reads the workflow entity catalogue (`WorkflowEntityDescriptor`): a lookup over the descriptors the API serves, the field a
 * `WorkflowEntityFieldRef` names, and that field's type as the validator and `workflowScopeAt` compare it. The editor resolves
 * the same field through the Surface Engine (`toSurfaceMetadata`) for its picker and display; this is only the typing view.
 * Invariant: a ref resolves by the descriptor field's `key` (the entity's uiFields key), never by display name or path.
 */
import { MetadataEntityFieldType } from '../../metadata/entity/decorators/model/metadata.entity.field.types';
import type { JsonSafeMetadata, JsonSafeMetadataOption } from '../surface.engine/json.safe.metadata.zod';
import { ActivityEntityName } from '../shared/activity.entity.name';
import type { WorkflowEntityFieldRef } from './workflow.element';
import type { WorkflowEntityDescriptor, WorkflowEntityDescriptorLookup, WorkflowEntityFieldDescriptor } from './workflow.entity.catalogue';

/** Field types whose value is a record of another entity, named by the field's typeData. */
const RECORD_TYPES = new Set<string>([MetadataEntityFieldType.Lookup, MetadataEntityFieldType.Parent, MetadataEntityFieldType.Site]);

/** A lookup over `descriptors` by entity name. Called by the workflow editor once it has loaded the catalogue. */
export function workflowEntityLookup(descriptors: readonly WorkflowEntityDescriptor[]): WorkflowEntityDescriptorLookup {

    const byEntity = new Map<string, WorkflowEntityDescriptor>(descriptors.map(descriptor => [descriptor.entity, descriptor]));
    return entity => byEntity.get(entity);
}

/** The descriptor field `ref` names, or undefined when the catalogue has no such entity or field. */
export function workflowEntityField(entities: WorkflowEntityDescriptorLookup, ref: WorkflowEntityFieldRef): WorkflowEntityFieldDescriptor | undefined {

    return entities(ref.entity)?.fields.find(field => field.key === ref.field);
}

/**
 * `field`'s type as a workflow value: its type, `arrayOf` for a many-valued field, the record entity a lookup or tag holds and
 * an enum's choices. Called by the validator to type a value an entity field holds and by `workflowScopeAt` to type its entries.
 */
export function workflowEntityFieldMetadata(field: WorkflowEntityFieldDescriptor): JsonSafeMetadata {

    const type = Array.isArray(field.type) ? field.type[0] : field.type;
    const entity = recordEntityOf(type, field.typeData);
    const options = enumOptionsOf(type, field.typeData);
    return {
        name: field.name,
        displayName: field.displayName,
        path: field.path,
        type,
        ...(Array.isArray(field.type) ? { arrayOf: type } : {}),
        ...(entity ? { entity } : {}),
        ...(options ? { options } : {}),
        ...(field.visualType ? { visualType: field.visualType } : {}),
    };
}

/** The entity a record-valued field holds: Tag for a tag field, else the lookup target its typeData names. */
function recordEntityOf(type: string, typeData: unknown): string | undefined {

    if (type === MetadataEntityFieldType.Tag) {
        return ActivityEntityName.Tag;
    }
    if (!RECORD_TYPES.has(type)) {
        return undefined;
    }
    if (typeof typeData === 'string') {
        return typeData;
    }
    const target = (typeData as { type?: unknown } | null | undefined)?.type;
    return typeof target === 'string'
        ? target
        : undefined;
}

/** An enum field's choices, from its typeData's `{ key, value }` entries. */
function enumOptionsOf(type: string, typeData: unknown): JsonSafeMetadataOption[] | undefined {

    if (type !== MetadataEntityFieldType.Enum || !Array.isArray(typeData)) {
        return undefined;
    }
    return typeData
        .filter((entry): entry is { key: string; value: string | number } => typeof entry?.value === 'string' || typeof entry?.value === 'number')
        .map(entry => ({ value: entry.value, label: String(entry.key) }));
}
